<template>
  <section class="admin-connection-tools">
    <button type="button" class="tools-toggle" :aria-expanded="open" @click="toggle">WireGuard / IP stack</button>
    <div v-if="open" class="tools-panel" aria-live="polite">
      <p>Settings apply to the entire phone connection.</p>
      <p v-if="busy">Loading connection settings...</p>
      <label v-if="subscriptions.length">Active subscription
          <select v-model="license" :disabled="working" @change="load">
            <option value="">Select a subscription</option>
            <option v-for="item in subscriptions" :key="item.uuid" :value="item.uuid">{{ item.label }}{{ item.limited ? ' (data allowance exhausted)' : '' }}</option>
          </select>
        </label>
      <template v-if="!busy && settings">
        <label>IP stack
          <select v-model="selected" :disabled="!settings.can_change_stack || working">
            <option disabled value="">Provider default</option>
            <option value="ipv4">IPv4 only</option>
            <option value="dual">Dual (IPv4 + IPv6)</option>
          </select>
        </label>
        <button type="button" :disabled="!settings.can_change_stack || working || !selected || selected === settings.stack" @click="save">{{ working ? 'Working...' : 'Save IP stack' }}</button>
        <p v-if="settings.reason">{{ settings.reason }}</p>
        <p v-if="settings.ipv4">IPv4: {{ settings.ipv4 }}</p>
        <p v-if="settings.ipv6">IPv6: {{ settings.ipv6 }}</p>
        <p v-else-if="settings.stack === 'dual'">IPv6 is not currently reported by this phone.</p>
        <button type="button" :disabled="!settings.can_wireguard || working" @click="download">Download WireGuard</button>
        <p v-if="!settings.can_wireguard">{{ settings.wireguard_reason || 'WireGuard requires an eligible provider plan and an active subscription.' }}</p>
        <p v-if="settings.metered">Data-capped WireGuard requires an exclusive phone and Dual stack. HTTP, SOCKS and WireGuard share the daily allowance.</p>
        <p v-if="settings.metered && settings.wireguard_active && selected === 'ipv4'">Saving IPv4 only disconnects this subscription's WireGuard configuration.</p>
      </template>
      <p v-if="error" role="alert">{{ error }}</p>
      <button v-if="error" type="button" :disabled="busy || working" @click="load">Retry</button>
    </div>
  </section>
</template>
<script setup>
/* global defineProps */
import { ref, watch } from 'vue';
import { useAccountStore } from '@/stores';
const props = defineProps({ serverName: { type: String, required: true } });
const subscriptions = ref([]);
const open = ref(false), busy = ref(false), working = ref(false), settings = ref(null), license = ref(''), selected = ref(''), error = ref('');
let sequence = 0;
const api = process.env.VUE_APP_VUE_API_URL;
async function request(action, name, body = {}) {
  const response = await fetch(api + '/admin/iproxy/connection-tools/' + action, {
    method: 'POST', cache: 'no-store', headers: { 'x-access-token': useAccountStore().user?.accessToken || '', 'Content-Type': 'application/json' },
    body: JSON.stringify({ serverName: name, ...body }), signal: AbortSignal.timeout(65000),
  });
  if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.message || 'Connection request failed. Retry.'); }
  return response;
}
async function load() {
  const current = ++sequence, name = props.serverName, uuid = license.value;
  busy.value = true; settings.value = null; error.value = '';
  try {
    const result = await (await request('status', name, { license: uuid || null })).json();
    if (current !== sequence || name !== props.serverName) return;
    subscriptions.value = result.subscriptions; settings.value = result; selected.value = result.stack || '';
  } catch (e) { if (current === sequence) error.value = e.message; }
  finally { if (current === sequence) busy.value = false; }
}
function toggle() { open.value = !open.value; if (open.value) load(); }
async function save() {
  if (working.value || !settings.value?.can_change_stack) return;
  const name = props.serverName; working.value = true; error.value = '';
  try { await request('stack', name, { license: license.value || null, stack: selected.value }); if (name === props.serverName) await load(); }
  catch (e) { if (name === props.serverName) error.value = e.message; }
  finally { working.value = false; }
}
async function download() {
  if (working.value || !settings.value?.can_wireguard) return;
  const name = props.serverName; working.value = true; error.value = ''; let url;
  try {
    const response = await request('wireguard', name, { license: license.value });
    if (name !== props.serverName) return;
    url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a'); link.href = url; link.download = 'phoneproxy-wireguard.conf'; document.body.appendChild(link); link.click(); link.remove(); await load();
  } catch (e) { if (name === props.serverName) error.value = e.message; }
  finally { if (url) setTimeout(() => URL.revokeObjectURL(url), 30000); working.value = false; }
}
watch(() => props.serverName, () => { ++sequence; settings.value = null; subscriptions.value = []; license.value = ''; error.value = ''; busy.value = false; if (open.value) load(); });
</script>
<style scoped>
.admin-connection-tools { width:100%; min-width:0; margin:10px 0; }
.tools-toggle { text-decoration:underline; font-weight:600; padding:8px; }
.tools-panel { border:1px solid #9ca3af; padding:12px; border-radius:6px; overflow-wrap:anywhere; }
.tools-panel label { display:block; margin-top:8px; }
.tools-panel select { display:block; width:100%; max-width:420px; padding:8px; background:#fff; color:#111827; border:1px solid #9ca3af; }
.tools-panel button { display:block; padding:8px 12px; margin-top:8px; border:1px solid #9ca3af; border-radius:4px; }
.tools-panel button:disabled,.tools-panel select:disabled { opacity:.5; cursor:not-allowed; }
.tools-panel p { margin:8px 0; }
.tools-panel [role=alert] { color:#dc2626; }
</style>
